import { Symptom, SymptomGroup, StageId } from '../types/content'

export const symptomGroups: SymptomGroup[] = [
  { id: 'body', label: 'In your body', note: 'Temperature, weight, joints, skin, heart.' },
  { id: 'mind', label: 'In your head', note: 'Mood, memory, focus, and the rage.' },
  { id: 'sleep', label: 'Sleep', note: 'Falling asleep, staying asleep, 3am.' },
  { id: 'cycle', label: 'Your cycle', note: 'Timing, bleeding, PMS, and what changed.' },
  { id: 'intimacy', label: 'Intimacy', note: 'Desire, comfort, dryness, pain, bladder.' },
]

export const symptoms: Symptom[] = [
  // Body
  {
    id: 'hot-flushes',
    label: 'Hot flushes',
    group: 'body',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'Falling estrogen narrows the temperature range your brain considers acceptable. A change of a few tenths of a degree now triggers a full cooling response — flushing, sweating, then a chill.',
    whatHelps:
      'Transdermal estradiol is the most effective treatment there is. Where hormones are not appropriate, non-hormonal options including fezolinetant and low-dose SSRIs meaningfully reduce frequency.',
    carePath: 'hormone-therapy',
  },
  {
    id: 'night-sweats',
    label: 'Night sweats',
    group: 'body',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'The same temperature-regulation change, happening while you are asleep. It usually wakes you shortly before you notice the heat, which is why it feels like insomnia first and sweating second.',
    whatHelps:
      'Treating the flush treats the sweat. Estradiol, non-hormonal alternatives, and getting the bedroom genuinely cold — most women underestimate how cold.',
    carePath: 'sleep-mood-fog',
  },
  {
    id: 'weight-middle',
    label: 'Weight settling around my middle',
    group: 'body',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'Falling estrogen redistributes fat from hips to abdomen and reduces insulin sensitivity. You can be eating and moving exactly as before and see your body change shape anyway. This is not a discipline failure.',
    whatHelps:
      'Protein and resistance training first — muscle is the lever here. Then insulin sensitivity, sleep, and where clinically appropriate, metabolic medication.',
    carePath: 'midlife-metabolism',
    commonlyDismissed: true,
  },
  {
    id: 'joint-pain',
    label: 'Joints that ache for no reason',
    group: 'body',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'Estrogen receptors sit in cartilage, tendon and synovium. When estrogen drops, stiffness and aching follow — often worst in the morning, often in hands and shoulders. It has a name: the musculoskeletal syndrome of menopause.',
    whatHelps:
      'Many women see joint pain improve on hormone therapy. Loading the tissue through progressive strength work is the other half.',
    carePath: 'bones-heart-long-view',
    commonlyDismissed: true,
  },
  {
    id: 'palpitations',
    label: 'Heart racing or skipping',
    group: 'body',
    stages: ['peri', 'menopause'],
    whatsHappening:
      'Estrogen fluctuation drives adrenaline surges, which the heart registers as pounding or fluttering — frequently at night, frequently alongside a flush.',
    whatHelps:
      'Cardiac causes get ruled out first, always. Once they are, this usually settles with hormone therapy or targeted non-hormonal treatment.',
    carePath: 'hormone-therapy',
  },
  {
    id: 'hair-skin',
    label: 'Hair thinning, skin changing',
    group: 'body',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'Collagen density falls by roughly a third in the first five years after menopause. Hair moves into a resting phase for longer, so shedding outpaces regrowth.',
    whatHelps:
      'Screening for thyroid and ferritin first, because both are common and both are fixable. Then topical and hormonal options.',
    carePath: 'midlife-metabolism',
  },

  // Mind
  {
    id: 'brain-fog',
    label: 'Brain fog — losing words',
    group: 'mind',
    stages: ['peri', 'menopause'],
    whatsHappening:
      'The brain is dense with estrogen receptors, particularly in the hippocampus and prefrontal cortex. Withdrawal affects verbal recall and working memory. Women reliably search for early dementia at this stage. Imaging studies show the brain adapts — this is a transition, not a decline.',
    whatHelps:
      'Sleep first, because fog and sleep loss compound each other. Hormone therapy helps many women; where it does not, the fog still lifts on its own timeline.',
    carePath: 'sleep-mood-fog',
    commonlyDismissed: true,
  },
  {
    id: 'anxiety',
    label: 'Anxiety that came out of nowhere',
    group: 'mind',
    stages: ['early', 'peri'],
    whatsHappening:
      'Progesterone metabolises into allopregnanolone, which acts on the same receptors as anti-anxiety medication. When progesterone falls, that natural calming buffer disappears. New-onset anxiety in your forties is one of the most common and most misread perimenopause signals.',
    whatHelps:
      'Cycle-timed or continuous micronised progesterone. Where indicated, an SSRI — but only after someone has actually considered your hormones.',
    carePath: 'sleep-mood-fog',
    commonlyDismissed: true,
  },
  {
    id: 'rage',
    label: 'Rage — disproportionate, frightening',
    group: 'mind',
    stages: ['early', 'peri'],
    whatsHappening:
      'Erratic estrogen destabilises serotonin while low progesterone removes the brake. The result is irritability with no gap between stimulus and reaction. Almost every woman in perimenopause describes this, and almost none of them say it out loud.',
    whatHelps:
      'Hormonal stabilisation, and knowing that this is chemistry rather than character. Naming it changes how it feels almost immediately.',
    carePath: 'sleep-mood-fog',
    commonlyDismissed: true,
  },
  {
    id: 'low-mood',
    label: 'Flat, tearful, not myself',
    group: 'mind',
    stages: ['early', 'peri', 'menopause'],
    whatsHappening:
      'Perimenopause carries genuinely elevated risk of depressive episodes, especially if you have had postnatal depression or severe PMS before. This is a hormonally-driven vulnerability window, not a personality change.',
    whatHelps:
      'A real assessment that considers both hormones and mood treatment, rather than defaulting to an antidepressant and closing the file.',
    carePath: 'sleep-mood-fog',
    commonlyDismissed: true,
  },
  {
    id: 'motivation',
    label: 'No drive, no interest, exhausted',
    group: 'mind',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'Fragmented sleep, falling estrogen and often low testosterone stack together. The fatigue is not proportional to what you did that day, which is the tell.',
    whatHelps:
      'Fix sleep, screen thyroid, ferritin and vitamin D, and assess hormone therapy. This one is very often reversible.',
    carePath: 'sleep-mood-fog',
  },

  // Sleep
  {
    id: 'wake-3am',
    label: 'Waking at 3am, every night',
    group: 'sleep',
    stages: ['early', 'peri', 'menopause'],
    whatsHappening:
      'Progesterone is sedating, and it goes first. Add a cortisol rise in the early hours and a temperature surge, and you wake at almost exactly the same time each night — alert, hot, and calculating how you will get through tomorrow.',
    whatHelps:
      'Micronised progesterone taken at night is both a hormone and a sleep aid. Estradiol handles the temperature side. Sleep hygiene alone rarely touches this, which is why being told to try lavender feels insulting.',
    carePath: 'sleep-mood-fog',
    commonlyDismissed: true,
  },
  {
    id: 'cant-fall-asleep',
    label: 'Can’t fall asleep, however tired',
    group: 'sleep',
    stages: ['peri', 'menopause'],
    whatsHappening:
      'Loss of the progesterone calming effect combined with a nervous system running hotter than it used to. Being exhausted and wired at the same time is characteristic.',
    whatHelps:
      'Progesterone at night, evening light and temperature discipline, and CBT-I where insomnia has become its own habit.',
    carePath: 'sleep-mood-fog',
  },
  {
    id: 'unrefreshing',
    label: 'Sleeping, but never rested',
    group: 'sleep',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'Micro-arousals from flushes fragment deep sleep without fully waking you. Your total hours look fine. The architecture underneath them is not.',
    whatHelps:
      'Reducing flushes usually restores sleep depth. Sleep apnoea also rises sharply after menopause and is badly under-diagnosed in women — worth screening.',
    carePath: 'sleep-mood-fog',
  },

  // Cycle
  {
    id: 'irregular',
    label: 'Cycles getting shorter or skipping',
    group: 'cycle',
    stages: ['peri'],
    whatsHappening:
      'Ovulation is becoming unreliable. A change of seven days or more in cycle length is the recognised clinical marker that perimenopause has started.',
    whatHelps:
      'This is the single most useful thing to track. Bring three months of cycle lengths to any consultation — it is worth more than a blood test.',
    carePath: 'perimenopause-early-signals',
  },
  {
    id: 'heavy-bleeding',
    label: 'Heavy or flooding periods',
    group: 'cycle',
    stages: ['peri'],
    whatsHappening:
      'Cycles without ovulation mean estrogen builds the uterine lining with no progesterone to hold it in check. It sheds all at once. Common in perimenopause — and also worth ruling out fibroids, polyps and, rarely, more serious causes.',
    whatHelps:
      'Progesterone, a hormonal IUD, or tranexamic acid. Ferritin gets checked, because iron deficiency from heavy bleeding causes its own exhaustion.',
    carePath: 'perimenopause-early-signals',
  },
  {
    id: 'worse-pms',
    label: 'PMS that turned vicious',
    group: 'cycle',
    stages: ['early', 'peri'],
    whatsHappening:
      'The same premenstrual pattern you have always had, amplified by wider hormone swings. Often the very first sign, years before anything else changes.',
    whatHelps:
      'Cycle-timed progesterone. Tracking which days are worst makes the pattern obvious and makes treatment precise.',
    carePath: 'perimenopause-early-signals',
    commonlyDismissed: true,
  },
  {
    id: 'migraines',
    label: 'New or worsening migraines',
    group: 'cycle',
    stages: ['peri', 'menopause'],
    whatsHappening:
      'Estrogen withdrawal is a migraine trigger. As swings get steeper, attacks cluster around the drop — usually just before bleeding.',
    whatHelps:
      'Steady transdermal estradiol smooths the drop. Delivery route matters here, particularly if you have migraine with aura.',
    carePath: 'hormone-therapy',
  },

  // Intimacy
  {
    id: 'dryness',
    label: 'Vaginal dryness or burning',
    group: 'intimacy',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'Vulvovaginal tissue is estrogen-dependent. Without it the tissue thins, loses elasticity and stops self-lubricating. Unlike hot flushes, this does not improve with time — it progresses. It has a name: genitourinary syndrome of menopause.',
    whatHelps:
      'Local vaginal estrogen. It is highly effective, safe for the overwhelming majority of women including most breast cancer survivors after discussion, and it is drastically under-prescribed.',
    carePath: 'intimacy-vaginal-health',
    commonlyDismissed: true,
  },
  {
    id: 'painful-sex',
    label: 'Sex hurts now',
    group: 'intimacy',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'Tissue thinning plus reduced elasticity plus, often, protective pelvic floor tension built up from anticipating pain. Nearly half of post-menopausal women experience this. Very few are ever asked about it.',
    whatHelps:
      'Local estrogen, moisturisers distinct from lubricants, and pelvic floor physiotherapy. This is one of the most treatable things on this entire list.',
    carePath: 'intimacy-vaginal-health',
    commonlyDismissed: true,
  },
  {
    id: 'low-libido',
    label: 'Desire has gone',
    group: 'intimacy',
    stages: ['peri', 'menopause', 'post'],
    whatsHappening:
      'Testosterone falls gradually through midlife, and pain, exhaustion and low mood suppress desire independently. Untangling which is driving it matters more than treating all of them at once.',
    whatHelps:
      'Treat pain and sleep first. Where distressing low desire persists, low-dose testosterone has good evidence in post-menopausal women, prescribed carefully and monitored.',
    carePath: 'intimacy-vaginal-health',
  },
  {
    id: 'urinary',
    label: 'Urgency, leaking, repeat UTIs',
    group: 'intimacy',
    stages: ['menopause', 'post'],
    whatsHappening:
      'The urethra and bladder neck are estrogen-dependent too. Thinning tissue and a changed vaginal microbiome make recurrent urinary tract infections far more likely after menopause.',
    whatHelps:
      'Local vaginal estrogen substantially reduces recurrent UTIs. Pelvic floor work for urgency and leaking. Neither is an inevitable part of ageing.',
    carePath: 'intimacy-vaginal-health',
    commonlyDismissed: true,
  },
]

export function getSymptom(id: string): Symptom | undefined {
  return symptoms.find((symptom) => symptom.id === id)
}

/** Weighted read of which stage the selection points to. Never presented as a diagnosis. */
export function likelyStage(selectedIds: string[]): StageId | null {
  if (selectedIds.length === 0) return null

  const tally: Record<StageId, number> = { early: 0, peri: 0, menopause: 0, post: 0 }

  selectedIds.forEach((id) => {
    const symptom = getSymptom(id)
    if (!symptom) return
    symptom.stages.forEach((stage) => {
      tally[stage] += 1 / symptom.stages.length
    })
  })

  return (Object.keys(tally) as StageId[]).reduce((best, stage) =>
    tally[stage] > tally[best] ? stage : best,
  )
}

/** The questions to take into a consultation, derived from what she selected. */
export function questionsToAsk(selectedIds: string[]): string[] {
  const base = [
    'Based on my age, cycle history and symptoms, do you think I am in perimenopause?',
    'What would you want to rule out before we call it that?',
  ]

  const specific: string[] = []
  const has = (id: string) => selectedIds.includes(id)

  if (has('hot-flushes') || has('night-sweats') || has('migraines')) {
    specific.push('Am I a candidate for transdermal estradiol, and if not, what is the reason?')
  }
  if (has('wake-3am') || has('anxiety') || has('worse-pms') || has('cant-fall-asleep')) {
    specific.push('Would micronised progesterone at night be appropriate for me?')
  }
  if (has('dryness') || has('painful-sex') || has('urinary')) {
    specific.push(
      'Can I start local vaginal estrogen? I understand it works differently from systemic hormone therapy.',
    )
  }
  if (has('low-libido')) {
    specific.push('Do you prescribe low-dose testosterone for women, and how do you monitor it?')
  }
  if (has('heavy-bleeding')) {
    specific.push('Can we check my ferritin, and rule out fibroids or polyps?')
  }
  if (has('weight-middle') || has('joint-pain')) {
    specific.push(
      'Could these be related to falling estrogen rather than just to age or my weight?',
    )
  }

  specific.push(
    'If my bloods come back “normal”, what does that actually rule out at my stage?',
    'What would you like me to track before my next appointment?',
  )

  return [...base, ...specific]
}
